import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { CloudFunctions } from "@services/firebase";
import * as AuthService from "@services/firebase/AuthService.js";

export const useAdminStore = defineStore("admin", () => {
  // State
  const isAdmin = ref(null); // null = unknown, true = admin, false = not admin
  const adminCheckInProgress = ref(false);
  const lastAdminCheck = ref(null);
  const adminCheckCacheTime = ref(5 * 60 * 1000); // 5 minutes cache
  const adminCheckPromise = ref(null);

  // Getters
  const getIsAdmin = computed(() => isAdmin.value);
  const isAdminCheckInProgress = computed(() => adminCheckInProgress.value);
  const isAdminKnown = computed(() => isAdmin.value !== null);

  const needsAdminCheck = computed(() => {
    if (isAdmin.value === null) return true;
    if (!lastAdminCheck.value) return true;
    return Date.now() - lastAdminCheck.value > adminCheckCacheTime.value;
  });

  // Actions
  async function checkAdminStatus(forceRefresh = false) {
    // Return cached result if valid and not forcing refresh
    if (
      !forceRefresh &&
      !needsAdminCheck.value &&
      !adminCheckInProgress.value
    ) {
      return isAdmin.value || false;
    }

    // Return existing promise if we are already checking
    if (adminCheckPromise.value) {
      return adminCheckPromise.value;
    }

    const promise = (async () => {
      try {
        adminCheckInProgress.value = true;

        // Check if auth is ready
        if (!AuthService.isAuthenticationReady()) {
          setIsAdmin(false);
          return false;
        }

        // Check if user is logged in
        const isLoggedIn = AuthService.isUserLoggedIn();
        if (!isLoggedIn) {
          setIsAdmin(false);
          return false;
        }

        // Call the admin check API
        const response = await CloudFunctions.userIsAdmin();
        const isAdminResult = response.success
          ? response.data.userIsAdmin
          : false;

        setIsAdmin(isAdminResult);
        return isAdminResult;
      } catch (error) {
        console.error("Error checking admin status:", error);
        setIsAdmin(false);
        return false;
      } finally {
        // Cleanup promise reference
        adminCheckPromise.value = null;
        // Ensure in-progress flag is cleared (setIsAdmin usually does this, but for safety)
        adminCheckInProgress.value = false;
      }
    })();

    adminCheckPromise.value = promise;
    return promise;
  }

  function setIsAdmin(value) {
    isAdmin.value = value;
    lastAdminCheck.value = Date.now();
    adminCheckInProgress.value = false;
  }

  function clearAdminStatus() {
    isAdmin.value = null;
    lastAdminCheck.value = null;
    adminCheckInProgress.value = false;
  }

  async function requireAdminAccess(operation = "this operation") {
    const allowed = await checkAdminStatus();
    if (!allowed) {
      throw new Error(`Admin access required for ${operation}`);
    }
  }

  async function checkAdminRedirect() {
    try {
      // Check if auth is ready
      if (!AuthService.isAuthenticationReady()) {
        return {
          path: "/",
          query: {
            error: "auth_loading",
            message: "Authentication loading...",
          },
        };
      }

      // Check if user is logged in
      const isLoggedIn = AuthService.isUserLoggedIn();
      if (!isLoggedIn) {
        return {
          path: "/",
          query: {
            error: "login_required",
            message: "Please log in to access admin features",
          },
        };
      }

      // Check admin status
      const allowed = await checkAdminStatus();
      if (!allowed) {
        return {
          path: "/",
          query: {
            error: "access_denied",
            message: "Admin access required",
          },
        };
      }

      return null; // No redirect needed
    } catch (error) {
      console.error("Error in admin redirect check:", error);
      return {
        path: "/",
        query: {
          error: "auth_error",
          message: "Unable to verify admin access",
        },
      };
    }
  }

  return {
    isAdmin,
    adminCheckInProgress,
    lastAdminCheck,
    adminCheckCacheTime,
    getIsAdmin,
    isAdminCheckInProgress,
    isAdminKnown,
    needsAdminCheck,
    checkAdminStatus,
    setIsAdmin,
    clearAdminStatus,
    requireAdminAccess,
    checkAdminRedirect,
  };
});
